import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spinner, SpinnerSize } from '@fluentui/react'
import { useTranslation } from 'react-i18next'
import { AxiosResponse } from 'axios'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { FlatTree, makeStyles, mergeClasses, MessageBar, MessageBarBody, tokens, useHeadlessFlatTree_unstable } from '@fluentui/react-components'
import useAxios from '../../hooks/useAxios'
import useSharePoint from '../../hooks/useSharePoint'
import { Destination } from '../../context/AxiosContext'
import { driveItemOverviewProperties } from '../../helpers/documentHelper'
import { PATIENT_RETRY_HEADER } from '../../helpers/graphRetry'
import { DocumentSortOrder } from '../../config/documentConfig'
import { DocumentOpenType } from '../../context/GlobalContext'
import useDocumentsOpen from '../../hooks/document/useDocumentsOpen'
import { stealthClick } from '../StealthLink'
import { useApplicationStyles } from '../../pages/App'
import FileDocumentSearchBox from '../../v2/pages/File/ViewFileTabs/FileDocumentSearchBox'
import SimpleDocumentFlatTreeItem from './SimpleDocumentFlatTreeItem'
import { selectAllVisibleFiles, selectRange, toggleSelect } from './tree/documentTreeSelection'
import { TreeRow } from './tree/documentTreeTypes'
import {
    compareDriveItemsFlat,
    defaultMessageKeys,
    DocumentFlatTreeMessageKeys,
    fetchFolderDriveItemTreeBatched,
    FolderDriveItem,
    mapDriveItem,
} from './DocumentFlatTree'

const useStyles = makeStyles({
    // Same virtualization trick as DocumentFlatTree: skip layout/paint for off-screen rows so
    // a 5000-file dossier stays responsive.
    virtualizedTree: {
        '& [role="treeitem"]': {
            contentVisibility: 'auto',
            containIntrinsicSize: '0 32px',
            cursor: 'pointer',
        },
    },
    listSpacing: {
        marginTop: tokens.spacingVerticalM,
    },
})

const PATIENT_RETRY_HEADERS = { SuppressErrors: 'true', [PATIENT_RETRY_HEADER]: 'true' }

// The dossier size itself is unknown before the crawl finishes, so "taking longer than
// expected" is detected purely by elapsed time. 10s matches the performance norm in HAM-4057.
const SLOW_LOAD_NOTICE_MS = 10_000

// Full crawl result for one dossier, cached by the host (FileDocumentOverview) so switching
// tree <-> list repeatedly does not re-crawl. The host drops the cache on every mutation.
export type DocumentFlatListCache = {
    rootId: string
    rootFolder: FolderDriveItem
    items: FolderDriveItem[]
}

export type DocumentFlatListProps = {
    rootId: string
    // Dossier reference sent along with crawl telemetry (see DocumentFlatTree).
    referenceNumber?: string
    sortOrder: DocumentSortOrder
    selectedOptions: string[]
    onOptionsSelect: (ids: string[], items: FolderDriveItem[]) => void
    setIsLoading?: (isLoading: boolean) => void
    // (allItems, displayedItems): allItems is the full crawl (folders included, for the
    // copy/move/create dialogs' folder pickers); displayedItems are the files in the exact
    // order the list renders them, which drives the preview's previous/next.
    onDriveItemsLoaded?: (allItems: FolderDriveItem[], displayedItems?: FolderDriveItem[]) => void
    setReloadDriveItems?: (setter: () => void) => void
    onPreviewClick?: (driveItem: FolderDriveItem) => void
    size?: 'small' | 'medium'
    messageKeys?: DocumentFlatTreeMessageKeys
    cachedData?: DocumentFlatListCache
    onDataFetched?: (cache: DocumentFlatListCache) => void
}

// Folder path from the dossier root down to the file's parent ("Correspondentie / 2024").
// Files directly in the dossier root return undefined — there is no folder to disambiguate.
const buildSourceFolderPath = (item: FolderDriveItem): string | undefined => {
    const segments: string[] = []
    let ancestor = item.parent
    while (ancestor?.parent) {
        segments.unshift(ancestor.displayName)
        ancestor = ancestor.parent
    }
    return segments.length > 0 ? segments.join(' \\ ') : undefined
}

export default function DocumentFlatList(props: DocumentFlatListProps): ReactElement {
    const axios = useAxios()
    const documentsOpen = useDocumentsOpen()
    const appStyles = useApplicationStyles()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { sharePoint } = useSharePoint()
    const messageKeys: DocumentFlatTreeMessageKeys = { ...defaultMessageKeys, ...props.messageKeys }

    const [errorMessage, setErrorMessage] = useState<{ message: string; intent: 'warning' | 'error' }>(undefined)
    const [rootFolder, setRootFolder] = useState<FolderDriveItem>(undefined)
    // Full crawl result: every file AND folder of the dossier, depth-first (folders travel
    // along for the dialogs' folder pickers; only files are rendered).
    const [allItems, setAllItems] = useState<FolderDriveItem[]>(undefined)
    const allItemsRef = useRef<FolderDriveItem[]>(undefined)
    const [filteredItems, setFilteredItems] = useState<FolderDriveItem[]>(undefined)
    const [loading, setLoading] = useState<boolean>(true)
    const [slowNotice, setSlowNotice] = useState<boolean>(false)
    const slowTimerRef = useRef<number>(undefined)
    const loadEpochRef = useRef(0)
    // driveId may arrive after mount; a ref keeps the crawl callback stable without refetching.
    const driveIdRef = useRef<string>(undefined)
    driveIdRef.current = sharePoint?.driveId

    const clearSlowTimer = useCallback(() => {
        if (slowTimerRef.current) {
            window.clearTimeout(slowTimerRef.current)
            slowTimerRef.current = undefined
        }
        setSlowNotice(false)
    }, [])

    const replaceAllItems = useCallback((next: FolderDriveItem[] | undefined) => {
        allItemsRef.current = next
        setAllItems(next)
    }, [])

    const reload = useCallback(
        (useCache: boolean) => {
            setErrorMessage(undefined)
            setFilteredItems(undefined)

            if (!props.rootId) {
                replaceAllItems(undefined)
                return
            }

            if (useCache && props.cachedData?.rootId === props.rootId) {
                setRootFolder(props.cachedData.rootFolder)
                replaceAllItems(props.cachedData.items)
                setLoading(false)
                props.setIsLoading?.(false)
                return
            }

            loadEpochRef.current += 1
            const epoch = loadEpochRef.current

            setLoading(true)
            props.setIsLoading?.(true)
            clearSlowTimer()
            slowTimerRef.current = window.setTimeout(() => setSlowNotice(true), SLOW_LOAD_NOTICE_MS)

            axios
                .get<DriveItem, AxiosResponse<DriveItem>>(`/items/${props.rootId}`, {
                    baseURL: Destination.Documents,
                    params: { $select: driveItemOverviewProperties.join(',') },
                    headers: PATIENT_RETRY_HEADERS,
                })
                .then(response => {
                    if (epoch !== loadEpochRef.current) {
                        return undefined
                    }
                    if (response.status !== 200) {
                        return Promise.reject(`${response.status}: ${response.statusText}`)
                    }
                    const root = mapDriveItem(response.data, undefined, response.data.id)
                    setRootFolder(root)
                    return fetchFolderDriveItemTreeBatched(
                        axios,
                        translate,
                        driveIdRef.current,
                        props.rootId,
                        false,
                        props.referenceNumber,
                        'DocumentFlatList.FetchTree',
                    ).then(items => {
                        if (epoch !== loadEpochRef.current) {
                            return undefined
                        }
                        replaceAllItems(items)
                        props.onDataFetched?.({ rootId: props.rootId, rootFolder: root, items })
                        setLoading(false)
                        props.setIsLoading?.(false)
                        clearSlowTimer()
                    })
                })
                .catch(error => {
                    if (epoch !== loadEpochRef.current) {
                        return
                    }
                    replaceAllItems([])
                    setLoading(false)
                    props.setIsLoading?.(false)
                    clearSlowTimer()
                    const errorStatus = error?.response?.status ?? error?.status
                    if (errorStatus === 404) {
                        setErrorMessage({
                            message: translate(messageKeys.folderNotFound) + ' ' + translate(messageKeys.folderNotFoundEdit),
                            intent: 'warning',
                        })
                    } else if (errorStatus === 403) {
                        setErrorMessage({ message: translate(messageKeys.folderNoAccess), intent: 'error' })
                    } else {
                        // fetchFolderDriveItemTreeBatched rejects 404/403 with an already
                        // translated message string; show it as-is.
                        setErrorMessage({ message: error?.toString?.() ?? String(error), intent: 'error' })
                    }
                })
        },
        [
            axios,
            clearSlowTimer,
            messageKeys,
            props.cachedData,
            props.onDataFetched,
            props.referenceNumber,
            props.rootId,
            props.setIsLoading,
            replaceAllItems,
            translate,
        ],
    )

    useEffect(() => {
        reload(true)
        return () => clearSlowTimer()
    }, [props.rootId])

    useEffect(() => {
        // A reload after a mutation must bypass the (stale) cache; onDataFetched refills it.
        props.setReloadDriveItems?.(() => reload(false))
    }, [])

    // The files in render order: filtered by search when active, always in the flat sort order.
    const displayedLeaves = useMemo<FolderDriveItem[] | undefined>(() => {
        const source = filteredItems ?? allItems
        if (!source) {
            return undefined
        }
        return source
            .filter(item => item.itemType === 'leaf')
            .sort(compareDriveItemsFlat(props.sortOrder))
            .map(item => ({ ...item, parentValue: undefined }))
    }, [allItems, filteredItems, props.sortOrder])

    const sourcePathById = useMemo(() => {
        const result = new Map<string, string | undefined>()
        for (const item of displayedLeaves ?? []) {
            result.set(item.id, buildSourceFolderPath(item))
        }
        return result
    }, [displayedLeaves])

    useEffect(() => {
        if (!allItems) {
            return
        }
        props.onDriveItemsLoaded?.(allItems, displayedLeaves)
    }, [allItems, displayedLeaves])

    // ===== Selection: same row-click model as DocumentFlatTree (HAM-3993), minus folders =====
    const listContainerRef = useRef<HTMLDivElement>(null)
    const anchorIdRef = useRef<string | null>(null)
    const pendingClickRef = useRef<{ nodeId: string; timer: number } | null>(null)
    const selectedIdsRef = useRef<string[]>(props.selectedOptions ?? [])
    useEffect(() => {
        selectedIdsRef.current = props.selectedOptions ?? []
    }, [props.selectedOptions])
    useEffect(
        () => () => {
            if (pendingClickRef.current) {
                window.clearTimeout(pendingClickRef.current.timer)
            }
        },
        [],
    )

    const treeRows = useMemo<TreeRow[]>(
        () => (displayedLeaves ?? []).map(item => ({ id: item.id, parentId: undefined, isFolder: false })),
        [displayedLeaves],
    )
    const visibleOrder = useMemo<string[]>(() => treeRows.map(row => row.id), [treeRows])

    const commitSelection = useCallback(
        (ids: Iterable<string>) => {
            const idArray = [...ids]
            selectedIdsRef.current = idArray
            const items = idArray.map(id => allItemsRef.current?.find(item => item.id === id)).filter(Boolean)
            props.onOptionsSelect?.(idArray, items)
        },
        [props.onOptionsSelect],
    )

    const applyToggle = useCallback(
        (nodeId: string) => {
            const result = toggleSelect(new Set(selectedIdsRef.current), nodeId)
            anchorIdRef.current = result.anchor
            commitSelection(result.next)
        },
        [commitSelection],
    )

    const flushPendingClick = useCallback(
        (apply: boolean) => {
            const pending = pendingClickRef.current
            if (!pending) {
                return
            }
            window.clearTimeout(pending.timer)
            pendingClickRef.current = null
            if (apply) {
                applyToggle(pending.nodeId)
            }
        },
        [applyToggle],
    )

    const handleListClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = event.target as HTMLElement
        const selectToggle = target.closest<HTMLElement>('[data-action="select-toggle"]')
        if (selectToggle?.dataset.nodeId) {
            flushPendingClick(true)
            applyToggle(selectToggle.dataset.nodeId)
            return
        }
        if (target.closest('button, a, [role="menuitem"], input')) {
            return
        }
        const row = target.closest<HTMLElement>('[data-node-id]')
        if (!row?.dataset.nodeId) {
            return
        }
        const nodeId = row.dataset.nodeId
        if (event.shiftKey) {
            flushPendingClick(true)
            const result = selectRange(treeRows, visibleOrder, anchorIdRef.current, nodeId, new Set(selectedIdsRef.current), false)
            anchorIdRef.current = result.anchor ?? nodeId
            commitSelection(result.next)
            return
        }
        if (event.detail > 1) {
            // Second click of a double-click: the dblclick handler opens the file.
            return
        }
        flushPendingClick(true)
        pendingClickRef.current = {
            nodeId,
            timer: window.setTimeout(() => {
                pendingClickRef.current = null
                applyToggle(nodeId)
            }, 200),
        }
    }

    const handleListDoubleClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = event.target as HTMLElement
        if (target.closest('button, a, [role="menuitem"], input')) {
            return
        }
        const row = target.closest<HTMLElement>('[data-node-id][data-node-type="file"]')
        if (!row?.dataset.nodeId) {
            return
        }
        // The two clicks that formed this double-click must not toggle the selection.
        flushPendingClick(false)
        // Row activation always opens (never downloads), but honors the configured target (APP vs. ONLINE).
        stealthClick(
            {
                id: row.dataset.nodeId,
                destination: Destination.Documents,
                documentsOpen: { action: DocumentOpenType.Open, target: documentsOpen.target },
            },
            axios,
        )
    }

    // Escape and Ctrl+A, document-wide — same behavior and guards as DocumentFlatTree.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            if (target?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"], [role="listbox"]')) {
                return
            }
            if (event.key === 'Escape') {
                flushPendingClick(false)
                if (selectedIdsRef.current.length > 0) {
                    anchorIdRef.current = null
                    commitSelection([])
                }
            } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
                event.preventDefault()
                flushPendingClick(false)
                const result = selectAllVisibleFiles(treeRows, visibleOrder)
                commitSelection(result.next)
            }
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [treeRows, visibleOrder, commitSelection, flushPendingClick])

    const flatTree = useHeadlessFlatTree_unstable(displayedLeaves ?? [], {})

    if (!props.rootId) {
        return (
            <MessageBar intent={'warning'}>
                <MessageBarBody>
                    {translate(messageKeys.noLink)} {translate(messageKeys.noLinkEdit)}
                </MessageBarBody>
            </MessageBar>
        )
    }

    if (errorMessage) {
        return <MessageBar intent={errorMessage.intent}>{errorMessage.message}</MessageBar>
    }

    if (loading || !allItems) {
        return (
            <div className={mergeClasses(appStyles.flexColumn)}>
                {slowNotice && (
                    <MessageBar intent={'info'}>
                        <MessageBarBody>{translate('file.pivot.document.list.slow')}</MessageBarBody>
                    </MessageBar>
                )}
                <Spinner label={translate('base.loading')} size={SpinnerSize.xSmall} labelPosition="right" />
            </div>
        )
    }

    return (
        <div className={mergeClasses(appStyles.flexColumn)}>
            {rootFolder && (
                <FileDocumentSearchBox
                    placeholderKey={'file.pivot.document.search.placeholder'}
                    componentProps={{ appearance: 'underline' }}
                    rootFolder={rootFolder}
                    driveItems={allItems}
                    setFilteredDriveItems={searchResults => {
                        setFilteredItems(searchResults)
                        props.onOptionsSelect?.([], [])
                    }}
                    reset={() => {
                        setFilteredItems(undefined)
                        props.onOptionsSelect?.([], [])
                    }}
                />
            )}
            {filteredItems === undefined && displayedLeaves?.length === 0 && (
                <MessageBar intent={'info'}>
                    <MessageBarBody>{translate('file.pivot.document.list.empty')}</MessageBarBody>
                </MessageBar>
            )}
            <div ref={listContainerRef} onClick={handleListClick} onDoubleClick={handleListDoubleClick} className={styles.listSpacing}>
                <FlatTree {...flatTree.getTreeProps()} aria-label={'FileDocumentList'} size={props.size} className={styles.virtualizedTree}>
                    {Array.from(flatTree.items(), flatTreeItem => {
                        const treeItemProps = flatTreeItem.getTreeItemProps()
                        return (
                            <SimpleDocumentFlatTreeItem
                                key={flatTreeItem.value}
                                {...treeItemProps}
                                item={treeItemProps}
                                flatTreeHook={flatTree}
                                itemType={'leaf'}
                                size={props.size}
                                rootId={props.rootId}
                                sortOrder={props.sortOrder}
                                selectedOptions={props.selectedOptions}
                                onOptionsSelect={props.onOptionsSelect}
                                sourceFolderPath={sourcePathById.get(flatTreeItem.value as string)}
                                onPreviewClick={props.onPreviewClick}
                            />
                        )
                    })}
                </FlatTree>
            </div>
        </div>
    )
}
